import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { RadioButton, RadioGroup } from '@cbrebuild/blocks';
import PercentDollarInput from '../../../nucleus/inputs/PercentDollarInput';
import Datepicker from '../../../nucleus/datepicker/datepicker';
import Select from '../../../nucleus/select/select';
import CompanyAutocomplete from '../../autocomplete/company-autocomplete';
import { updateVoucher } from '../../../redux/actions/voucher/voucher-actions';
import AddressForm from '../AddressForm';
import useFormState, { EVENT_VALUE_TYPES } from '../../../hooks/useFormState';
import { updateCommission, updateCommissionVendor } from '../../../redux/actions/outside-broker-commissions-action';
import companiesService from '../../../services/companies-service';
import { selectTotalCommissions } from '../../../redux/selectors/voucher-selectors';

const selectOutsideBrokerById = (state, id) => (
  state.outsideBrokerCommissions.commissions.find(commission => commission.id === id)
);

const typeOverrides = {
  voucher_gross_commission_percent: EVENT_VALUE_TYPES.FLOAT,
  gross_commission: EVENT_VALUE_TYPES.FLOAT,
};

const OUTSIDE_BROKER_ADDRESS_TOOLTIP = {
  title: 'Outside Broker Address',
  text: 'The address entered for the Outside Broker should always be the Remit To address where their payment should be mailed (matching the W-9).',
};

const asString = value => (typeof value === 'string' ? value : '');

const parseAutocompleteCompany = (company) => {
  if (!company || typeof company === 'string') {
    return null;
  }

  const address = company.address || {};
  return {
    name: asString(company.name),
    country: asString(address.country) || asString(company.country) || 'United States',
    address: asString(address.address) || asString(company.address),
    city: asString(address.city) || asString(company.city),
    territory: asString(address.territory) || asString(company.territory),
    postal: asString(address.postal) || asString(company.postal),
    pre_agreement_id: '',
  };
};

const OutsideBrokerItem = ({
  id,
  dealId,
  voucherId,
  showValidation,
  outside_broker_is_percent,
}) => {
  const dealType = useSelector(state => state.deal.deal?.deal_type);
  const isEarlyInvoice = useSelector(state => !!state.voucher.voucher?.is_early_invoice);
  const commission = useSelector(state => selectOutsideBrokerById(state, id)) || {};
  const voucherBrokerRepRoleOptions = useSelector(state => state.options.voucher_broker_rep_role)
    .filter((option) => {
      if (!option || option.display_name.match('NONE')) return false;
      switch (dealType) {
        case 'sale':
          return option.display_name.match(/Buyer|Seller/);
        case 'lease':
          return option.display_name.match(/Landlord|Tenant/);
        default:
          return !option.display_name.match(/Buyer|Seller|Landlord|Tenant|Client/);
      }
    });
  const baseTotal = useSelector(selectTotalCommissions);
  const dispatch = useDispatch();
  const {
    gross_commission, license_expdt, license_code, outside_broker_name, paid_by_cbre, partner,
    voucher_broker_rep_role, voucher_gross_commission_percent,
    onStateChange: onCommissionChange, onStateChanged: onCommissionChanged,
  } = useFormState({
    partner: commission.partner,
    paid_by_cbre: commission.paid_by_cbre,
    voucher_broker_rep_role: commission.voucher_broker_rep_role,
    outside_broker_name: commission.outside_broker_name,
    outside_broker_email: commission.outside_broker_email,
    outside_broker_phone_number: commission.outside_broker_phone_number,
    outside_broker_title: commission.outside_broker_title,
    license_expdt: commission.license_expdt,
    license_code: commission.license_code,
    voucher_gross_commission_percent: commission.voucher_gross_commission_percent,
    gross_commission: commission.gross_commission,
  }, changes => dispatch(updateCommission(id, changes)), typeOverrides, { rowType: 'outside_broker', rowId: id });

  const {
    address, address2, city, name: vendorName, postal, territory, country,
    pre_agreement_id,
    onStateChange: onVendorChange, onStateChanged: onVendorChanged,
  } = useFormState({
    address: commission.vendor?.address || '',
    address2: commission.vendor?.address2 || '',
    city: commission.vendor?.city || '',
    name: commission.vendor?.name || '',
    postal: commission.vendor?.postal || '',
    territory: commission.vendor?.territory || '',
    country: commission.vendor?.country || '',
    pre_agreement_id: commission.vendor?.pre_agreement_id || '',
    // updates on form change
  }, async (changes, current, patchCurrent) => {
    const patch = {};
    if (changes.country && !('territory' in changes)) {
      patch.territory = '';
    }
    patchCurrent(patch);

    const vendorPatch = { ...changes, ...patch };

    // Angular parity: edit selected/typed company on existing vendor via PATCH.
    if (commission.vendor?.id) {
      dispatch(updateCommissionVendor(dealId, commission.vendor.id, vendorPatch));
      return;
    }

    // Fallback: if vendor was missing, create one and bind it to this commission.
    if (changes.name) {
      const update = Object.keys(current).reduce((acc, key) => {
        acc[key] = current[key].value;
        return acc;
      }, {});
      const company = await companiesService.createCompany({ ...update, ...patch, name: changes.name });
      patchCurrent(company);
      dispatch(updateCommission(id, { vendor: company.id }));
    }
    // TODO: voucher_gross_commission_percent updates the other commissions?
  }, {}, { rowType: 'outside_broker_vendor', rowId: id });

  const selectedCompanyDataRef = useRef(null);
  const latestCompanyInputRef = useRef(vendorName.value || '');

  // update voucher
  const onUpdateVoucher = ({ target: { name, value } }) => {
    dispatch(updateVoucher(voucherId, { [name]: value }));
  };

  const onCommissionRadioChanged = fieldName => ({ target: { value } }) => {
    onCommissionChanged({
      target: {
        name: fieldName,
        value: value === 'true',
        type: 'text',
      },
    });
  };

  const onCompanyAutocompleteChange = (input) => {
    if (typeof input === 'string') {
      selectedCompanyDataRef.current = null;
      latestCompanyInputRef.current = input;
      onVendorChange({
        target: {
          name: 'name',
          value: input,
          type: 'text',
        },
      });
      return;
    }

    const mappedCompany = parseAutocompleteCompany(input);
    if (!mappedCompany) {
      return;
    }

    selectedCompanyDataRef.current = mappedCompany;
    latestCompanyInputRef.current = mappedCompany.name;

    onVendorChange({
      target: {
        name: ['name', 'country', 'address', 'city', 'territory', 'postal', 'pre_agreement_id'],
        value: mappedCompany,
        type: 'text',
      },
    });
  };

  const onCompanyAutocompleteBlur = () => {
    const selectedCompanyData = selectedCompanyDataRef.current;
    if (selectedCompanyData) {
      selectedCompanyDataRef.current = null;
      onVendorChanged({
        target: {
          name: ['name', 'country', 'address', 'city', 'territory', 'postal', 'pre_agreement_id'],
          value: selectedCompanyData,
          type: 'text',
        },
      });
      return;
    }

    onVendorChanged({
      target: {
        name: 'name',
        value: latestCompanyInputRef.current,
        type: 'text',
      },
    });
  };

  // validation
  const invalidPaidByCbre = !isEarlyInvoice && paid_by_cbre.value === null;
  const invalidOutsideBrokerName = !isEarlyInvoice && (showValidation || outside_broker_name.dirty) && !outside_broker_name.value;
  const invalidGrossCommission = !isEarlyInvoice && (showValidation || gross_commission.dirty) && !gross_commission.value;

  const invalidRepRole = !isEarlyInvoice && (showValidation || voucher_broker_rep_role.dirty) && !voucher_broker_rep_role.value;

  return (
    <div className="outside-broker-item">
      <legend className="secondary-legend">
        Is this outside broker a CBRE affiliate?
      </legend>
      <RadioGroup
        name={`outside-broker-${id}-partner`}
        selectedValue={`${partner.value}`}
        className="radioVal"
        onChange={onCommissionRadioChanged('partner')}
        orientation="vertical"
      >
        <RadioButton value="false">No</RadioButton>
        <RadioButton value="true">Yes</RadioButton>
      </RadioGroup>
      <legend className="secondary-legend">
        {!isEarlyInvoice && <span className={`${invalidPaidByCbre ? 'asterisk-error' : ''}`}>* </span>}
        How is this outside broker being paid?
      </legend>
      <RadioGroup
        name={`outside-broker-${id}-paid-by-cbre`}
        selectedValue={`${paid_by_cbre.value}`}
        className={`${invalidPaidByCbre ? 'radiobutton-error' : ''} radioVal`}
        onChange={onCommissionRadioChanged('paid_by_cbre')}
        orientation="vertical"
      >
        <RadioButton value="true">Paid by CBRE</RadioButton>
        <RadioButton value="false">Paid directly</RadioButton>
      </RadioGroup>
      {invalidPaidByCbre ? <p className="asterisk-error"> How is outside broker being paid is required</p> : ''}
      <div className="field outside-broker-company-field">
        <label>Company</label>
        <CompanyAutocomplete
          placeholder="Search or enter company name"
          initSearchTerm={vendorName.value || ''}
          onChange={onCompanyAutocompleteChange}
          onBlur={onCompanyAutocompleteBlur}
          dataE2e={`outside-broker-company-${id}`}
          useSearchInputType={false}
        />
      </div>
      <div className="field outside-broker-full-name-field">
        <label>{!isEarlyInvoice && <span className={`${invalidOutsideBrokerName ? 'asterisk-error' : ''}`}>* </span>} Full Name</label>
        <input
          className={invalidOutsideBrokerName ? 'errorInput' : ''}
          type="text"
          name="outside_broker_name"
          value={outside_broker_name.value || ''}
          onChange={onCommissionChange}
          onBlur={onCommissionChanged}
        />
        {invalidOutsideBrokerName ? <p className="asterisk-error">Full Name is required</p> : ''}
      </div>
      <AddressForm
        address={address.value}
        address2={address2.value}
        city={city.value}
        country={country.value}
        postal={postal.value}
        territory={territory.value}
        preAgreementId={pre_agreement_id.value}
        preAgreementIdInputId={`outside-broker-${id}-pre-agreement-id`}
        onChange={e => (e.target.type === 'select-one' ? onVendorChanged(e) : onVendorChange(e))}
        onBlur={onVendorChanged}
        isRequired={!isEarlyInvoice}
        showValidation={!isEarlyInvoice && showValidation}
        showAddressLine2={false}
        addressTooltip={OUTSIDE_BROKER_ADDRESS_TOOLTIP}
      />
      <div className="field field-select">
        <label>
          {!isEarlyInvoice && <span className={`${invalidRepRole ? 'asterisk-error' : ''}`}>* </span>}
          Rep Role
        </label>
        <Select
          defaultOption={voucherBrokerRepRoleOptions.find(o => o.value === voucher_broker_rep_role.value)}
          onChange={({ value }) => onCommissionChanged({
            target: { value, type: 'text', name: 'voucher_broker_rep_role' },
          })}
          options={voucherBrokerRepRoleOptions}
          className={invalidRepRole ? 'selectErrorInput' : ''}
          errorMessage={invalidRepRole ? 'Rep Role is required' : ''}
        />
      </div>
      <div className="form-row form-row-datepicker">
        <div className="field-group col-2">
          <label><span> License Expiration Date</span></label>
          <Datepicker
            date={license_expdt.value || ''}
            onDateChange={date => onCommissionChanged({ target: { value: date, name: 'license_expdt', type: 'date' } })}
          />
        </div>
        <div className="field-group col-2">
          <label><span> License #</span></label>
          <input
            type="text"
            name="license_code"
            value={license_code.value || ''}
            onChange={onCommissionChange}
            onBlur={onCommissionChanged}
          />
        </div>
      </div>
      <PercentDollarInput
        isRequired={invalidGrossCommission}
        label={`${!isEarlyInvoice && !invalidGrossCommission ? '* ' : ''}amount`}
        name={{
          percentage: 'voucher_gross_commission_percent',
          value: 'gross_commission',
          select: 'outside_broker_is_percent',
        }}
        className={invalidGrossCommission ? 'errorInput' : ''}
        onTypeChange={onUpdateVoucher}
        onChange={onCommissionChange}
        onBlur={onCommissionChanged}
        baseTotal={baseTotal}
        percentage={voucher_gross_commission_percent.value}
        value={gross_commission.value}
        isPercent={outside_broker_is_percent}
      />
    </div>
  );
};

OutsideBrokerItem.propTypes = {
  id: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  showValidation: PropTypes.bool.isRequired,
  dealId: PropTypes.number.isRequired,
  voucherId: PropTypes.number.isRequired,
  outside_broker_is_percent: PropTypes.bool.isRequired,
};

export default OutsideBrokerItem;
